import React, { useState, useEffect, useRef } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { logoutUser } from "../api/auth";
import { useVipStore } from "../context/VipContext";

export default function VipNavbar() {
  const navigate = useNavigate();
  const location = useLocation();
  const { selectedVip } = useVipStore();

  const [showNavbar, setShowNavbar] = useState(
    typeof window !== "undefined" ? window.innerWidth <= 991 : false
  );
  const [leftMenuOpen, setLeftMenuOpen] = useState(false);
  const [userDropOpen, setUserDropOpen] = useState(false);

  const leftMenuRef = useRef(null);
  const userRef = useRef(null);

  const vipName = selectedVip?.name || "Vip";
  const vipEmail = selectedVip?.email || "vip@gmail.com";

  /* RESPONSIVE WIDTH CONTROL */
  useEffect(() => {
    const handleResize = () => {
      const isMobile = window.innerWidth <= 991;
      setShowNavbar(isMobile);

      if (!isMobile) {
        setLeftMenuOpen(false);
        setUserDropOpen(false);
      }
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    window.addEventListener("orientationchange", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("orientationchange", handleResize);
    };
  }, []);

  /* CLOSE DROPDOWNS ON ROUTE CHANGE */
  useEffect(() => {
    setLeftMenuOpen(false);
    setUserDropOpen(false);
  }, [location.pathname]);

  /* OUTSIDE CLICK CLOSE */
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (leftMenuRef.current && !leftMenuRef.current.contains(e.target)) {
        setLeftMenuOpen(false);
      }
      if (userRef.current && !userRef.current.contains(e.target)) {
        setUserDropOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, []);

  /* LOGOUT */
  const handleLogout = () => {
    logoutUser();
    localStorage.clear();
    sessionStorage.clear();
    navigate("/login");
  };

  /* HIDE ON DESKTOP (sidebar takes over) */
  if (!showNavbar) return null;

  return (
    <>
      {/* Modern, Animated & Matching Mobile Navbar */}
      <style>
        {`
          @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');

          .vip-mobile-navbar {
            display: flex;
            align-items: center;
            justify-content: space-between;
            padding: 12px 20px;
            background: linear-gradient(135deg, rgba(102, 126, 234, 0.95) 0%, rgba(118, 75, 162, 0.95) 100%);
            backdrop-filter: blur(12px);
            border-bottom: 1px solid rgba(255, 255, 255, 0.15);
            position: sticky;
            top: 0;
            z-index: 1100;
            font-family: 'Inter', sans-serif;
            box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
          }

          .navbar-left {
            display: flex;
            align-items: center;
            gap: 16px;
            position: relative;
          }

          .menu-toggle-btn {
            background: rgba(255, 255, 255, 0.2);
            border: none;
            color: #ffffff;
            font-size: 26px;
            width: 44px;
            height: 44px;
            border-radius: 12px;
            cursor: pointer;
            display: flex;
            align-items: center;
            justify-content: center;
            transition: all 0.3s ease;
            backdrop-filter: blur(10px);
            box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
          }

          .menu-toggle-btn:hover {
            background: rgba(255, 255, 255, 0.3);
            transform: scale(1.08);
          }

          .navbar-brand {
            font-size: 20px;
            font-weight: 800;
            color: #ffffff;
            letter-spacing: 0.5px;
            text-shadow: 0 2px 8px rgba(0,0,0,0.3);
          }

          .mobile-dropdown {
            position: absolute;
            top: 58px;
            left: 0;
            width: 260px;
            max-height: 70vh;
            overflow-y: auto;
            background: rgba(255, 255, 255, 0.98);
            backdrop-filter: blur(15px);
            border-radius: 16px;
            box-shadow: 0 15px 35px rgba(0, 0, 0, 0.25);
            border: 1px solid rgba(255, 255, 255, 0.3);
            animation: slideDown 0.35s ease-out;
            z-index: 1200;
          }

          @keyframes slideDown {
            from {
              opacity: 0;
              transform: translateY(-20px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }

          .mobile-menu-item {
            display: block;
            padding: 16px 20px;
            text-decoration: none;
            color: #2d3748;
            font-weight: 600;
            font-size: 15px;
            border-bottom: 1px solid rgba(0, 0, 0, 0.08);
            transition: all 0.25s ease;
          }

          .mobile-menu-item:hover {
            background: rgba(102, 126, 234, 0.12);
            color: #667eea;
            padding-left: 28px;
          }

          .mobile-menu-item.active {
            background: rgba(102, 126, 234, 0.18);
            color: #667eea;
            font-weight: 700;
          }

          .navbar-right {
            position: relative;
          }

          .profile-btn {
            background: none;
            border: none;
            padding: 0;
            cursor: pointer;
          }

          .profile-avatar {
            width: 42px;
            height: 42px;
            border-radius: 50%;
            object-fit: cover;
            border: 3px solid rgba(255, 255, 255, 0.4);
            box-shadow: 0 4px 15px rgba(0, 0, 0, 0.3);
            transition: all 0.3s ease;
          }

          .profile-btn:hover .profile-avatar {
            transform: scale(1.1);
            border-color: #ffffff;
          }

          .profile-dropdown {
            position: absolute;
            right: 0;
            top: 58px;
            width: 260px;
            background: rgba(255, 255, 255, 0.98);
            backdrop-filter: blur(15px);
            border-radius: 16px;
            box-shadow: 0 15px 35px rgba(0, 0, 0, 0.25);
            border: 1px solid rgba(255, 255, 255, 0.3);
            overflow: hidden;
            animation: slideDown 0.35s ease-out;
            z-index: 1200;
          }

          .profile-header {
            display: flex;
            align-items: center;
            gap: 14px;
            padding: 18px 20px;
            background: linear-gradient(135deg, #667eea, #764ba2);
            color: #ffffff;
          }

          .profile-big-avatar {
            width: 52px;
            height: 52px;
            border-radius: 50%;
            object-fit: cover;
            border: 3px solid rgba(255, 255, 255, 0.4);
          }

          .profile-info h4 {
            margin: 0;
            font-size: 16px;
            font-weight: 700;
          }

          .profile-info small {
            opacity: 0.9;
            font-size: 13px;
          }

          .profile-menu-item {
            display: block;
            padding: 14px 20px;
            text-decoration: none;
            color: #2d3748;
            font-weight: 600;
            font-size: 15px;
            transition: all 0.25s ease;
          }

          .profile-menu-item:hover {
            background: rgba(102, 126, 234, 0.12);
            color: #667eea;
            padding-left: 28px;
          }

          .logout-mobile-btn {
            width: 100%;
            padding: 16px 20px;
            background: #ff3b30;
            color: #ffffff;
            border: none;
            font-weight: 700;
            font-size: 15px;
            cursor: pointer;
            transition: all 0.3s ease;
          }

          .logout-mobile-btn:hover {
            background: #e6332a;
          }

          /* Custom Scrollbar for Dropdowns */
          .mobile-dropdown::-webkit-scrollbar,
          .profile-dropdown::-webkit-scrollbar {
            width: 6px;
          }

          .mobile-dropdown::-webkit-scrollbar-thumb,
          .profile-dropdown::-webkit-scrollbar-thumb {
            background: rgba(102, 126, 234, 0.4);
            border-radius: 3px;
          }
        `}
      </style>

      <nav className="vip-mobile-navbar">
        {/* LEFT: Menu + Brand */}
        <div className="navbar-left" ref={leftMenuRef}>
          <button
            className="menu-toggle-btn"
            aria-label="Open menu"
            onClick={() => {
              setLeftMenuOpen((p) => !p);
              setUserDropOpen(false);
            }}
          >
            <i className="fas fa-bars"></i>
          </button>

          <span className="navbar-brand">VIP Panel</span>

          {leftMenuOpen && (
            <div className="mobile-dropdown">
              <NavLink
                to="/vipdashboard"
                className={({ isActive }) => `mobile-menu-item ${isActive ? "active" : ""}`}
                end
              >
                <i className="fas fa-home" style={{ marginRight: "12px", color: "#667eea" }}></i>
                Dashboard
              </NavLink>

              <NavLink
                to="/assignofficers"
                className={({ isActive }) => `mobile-menu-item ${isActive ? "active" : ""}`}
              >
                <i className="fas fa-user-shield" style={{ marginRight: "12px", color: "#667eea" }}></i>
                Assigned Officers
              </NavLink>
              <NavLink
                to="/viphistory"
                className={({ isActive }) => `mobile-menu-item ${isActive ? "active" : ""}`}
              >
                <i className="fas fa-history" style={{ marginRight: "12px", color: "#667eea" }}></i>
                Officer Duty History
              </NavLink>
              <NavLink
                to="/vipNotification"
                className={({ isActive }) => `mobile-menu-item ${isActive ? "active" : ""}`}
              >
                <i className="fas fa-bell" style={{ marginRight: "12px", color: "#667eea" }}></i>
                Notifications
              </NavLink>




               <NavLink
                to="/vipcomplaints"
                className={({ isActive }) => `mobile-menu-item ${isActive ? "active" : ""}`}
                >
                <i className="fas fa-flag" style={{ marginRight: "12px", color: "#667eea" }}></i>
                Complaints
                </NavLink> 

              <NavLink
                to="/vipfeedbacks"
                className={({ isActive }) => `mobile-menu-item ${isActive ? "active" : ""}`}
              >
                <i className="fas fa-comment-dots" style={{ marginRight: "12px", color: "#667eea" }}></i>
                Feedbacks
              </NavLink>

              <NavLink
                to="/vipduty"
                className={({ isActive }) => `mobile-menu-item ${isActive ? "active" : ""}`}
              >
                <i className="fas fa-tasks" style={{ marginRight: "12px", color: "#667eea" }}></i>
                Duty Management
              </NavLink>
                <NavLink
                  to="/vipprofile"
                  className={({ isActive }) => `mobile-menu-item ${isActive ? "active" : ""}`}
                >
                  <i className="fas fa-user" style={{ marginRight: "12px", color: "#667eea" }}></i>
                  My Profile
                </NavLink>
            </div>
          )}
        </div>

        {/* RIGHT: Profile */}
        <div className="navbar-right" ref={userRef}>
          <button
            className="profile-btn"
            aria-label="Open profile"
            onClick={() => {
              setUserDropOpen((p) => !p);
              setLeftMenuOpen(false);
            }}
          >
            <img
              src="/assets/img/avtar.jpg"
              alt="Vip"
              className="profile-avatar"
            />
          </button>

          {userDropOpen && (
            <div className="profile-dropdown">
              <div className="profile-header">
                <img
                  src="/assets/img/avtar.jpg"
                  alt="Vip"
                  className="profile-big-avatar"
                />
                <div className="profile-info">
                  <h4>{vipName}</h4>
                  <small>{vipEmail}</small>
                </div>
              </div>

              <NavLink
                to="/vipprofile"
                className="profile-menu-item"
              >
                <i className="fas fa-user-circle" style={{ marginRight: "10px" }}></i>
                My Profile
              </NavLink>

              <button onClick={handleLogout} className="logout-mobile-btn">
                <i className="fas fa-sign-out-alt" style={{ marginRight: "10px" }}></i>
                Logout
              </button>
            </div>
          )}
        </div>
      </nav>
    </>
  );
}


// import React, { useState, useEffect, useRef } from "react";
// import { NavLink, useNavigate, useLocation } from "react-router-dom";
// import { logoutUser } from "../api/auth";
// import { useVipStore } from "../context/VipContext";

// export default function VipNavbar() {
//   const navigate = useNavigate();
//   const location = useLocation();
//   const { selectedVip } = useVipStore();

//   const [showNavbar, setShowNavbar] = useState(
//     typeof window !== "undefined" ? window.innerWidth <= 991 : false
//   );
//   const [leftMenuOpen, setLeftMenuOpen] = useState(false);
//   const [userDropOpen, setUserDropOpen] = useState(false);

//   const leftMenuRef = useRef(null);
//   const userRef = useRef(null);

//   const vipName = selectedVip?.name || "Vip";
//   const vipEmail = selectedVip?.email || "vip@gmail.com";

//   /*  RESPONSIVE WIDTH CONTROL */
//   useEffect(() => {
//     const handleResize = () => {
//       const isMobile = window.innerWidth <= 991;
//       setShowNavbar(isMobile);

//       if (!isMobile) {
//         setLeftMenuOpen(false);
//         setUserDropOpen(false);
//       }
//     };

//     handleResize();
//     window.addEventListener("resize", handleResize);
//     window.addEventListener("orientationchange", handleResize);

//     return () => {
//       window.removeEventListener("resize", handleResize);
//       window.removeEventListener("orientationchange", handleResize);
//     };
//   }, []);

//   /*  CLOSE DROPDOWNS ON ROUTE CHANGE */
//   useEffect(() => {
//     setLeftMenuOpen(false);
//     setUserDropOpen(false);
//   }, [location.pathname]);

//   /*  OUTSIDE CLICK CLOSE */
//   useEffect(() => {
//     const handleClickOutside = (e) => {
//       if (leftMenuRef.current && !leftMenuRef.current.contains(e.target)) {
//         setLeftMenuOpen(false);
//       }
//       if (userRef.current && !userRef.current.contains(e.target)) {
//         setUserDropOpen(false);
//       }
//     };

//     document.addEventListener("mousedown", handleClickOutside);
//     document.addEventListener("touchstart", handleClickOutside);

//     return () => {
//       document.removeEventListener("mousedown", handleClickOutside);
//       document.removeEventListener("touchstart", handleClickOutside);
//     };
//   }, []);

//   /*  LOGOUT */
//   const handleLogout = () => {
//     logoutUser();
//     localStorage.clear();
//     sessionStorage.clear();
//     navigate("/login");
//   };

//   /*  HIDE ON DESKTOP */
//   if (!showNavbar) return null;

//   return (
//     <nav style={styles.navbar}>
//       {/*  LEFT SIDE : MENU + BRAND */}
//       <div style={styles.leftBox} ref={leftMenuRef}>
//         <button
//           style={styles.menuBtn}
//           aria-label="Open menu"
//           onClick={() => {
//             setLeftMenuOpen((p) => !p);
//             setUserDropOpen(false);
//           }}
//         >
//           ☰
//         </button>

//         <span style={styles.brand}>VIP Panel</span>

//         {leftMenuOpen && (
//           <div style={styles.leftDropdown}>
//             <NavLink to="/vipdashboard" style={styles.menuItem}>
//               Dashboard
//             </NavLink>

//             <NavLink to="/assignofficers" style={styles.menuItem}>
//               Assigned Officers
//             </NavLink>

//             <NavLink to="/vipprofile" style={styles.menuItem}>
//               My Profile
//             </NavLink>

//             <NavLink to="/viphistory" style={styles.menuItem}>
//               Officer Duty History
//             </NavLink>

//             <NavLink to="/vipNotification" style={styles.menuItem}>
//               Notifications
//             </NavLink>

//             <NavLink to="/vipcomplaints" style={styles.menuItem}>
//               Complaints
//             </NavLink>

//             <NavLink to="/vipfeedbacks" style={styles.menuItem}>
//               Feedbacks
//             </NavLink>

//             <NavLink to="/vipduty" style={styles.menuItem}>
//               Duty Management
//             </NavLink>
//           </div>
//         )}
//       </div>

//       {/*  RIGHT SIDE : PROFILE ICON */}
//       <div style={styles.userBox} ref={userRef}>
//         <button
//           style={styles.profileIconBtn}
//           aria-label="Open profile"
//           onClick={() => {
//             setUserDropOpen((p) => !p);
//             setLeftMenuOpen(false);
//           }}
//         >
//           <img
//             src="/assets/img/avtar.jpg"
//             alt="Vip"
//             style={styles.avatar}
//           />
//         </button>

//         {userDropOpen && (
//           <div style={styles.userDropdown}>
//             <div style={styles.userHeader}>
//               <img
//                 src="/assets/img/avtar.jpg"
//                 alt="Vip"
//                 style={styles.userBigAvatar}
//               />
//               <div>
//                 <strong>{vipName}</strong>
//                 <br />
//                 <small>{vipEmail}</small>
//               </div>
//             </div>

//             <NavLink to="/vipprofile" style={styles.userItem}>
//               My Profile
//             </NavLink>

//             <button onClick={handleLogout} style={styles.logoutBtn}>
//               Logout
//             </button>
//           </div>
//         )}
//       </div>
//     </nav>
//   );
// }

// /* ===============================
//     VIP NAVBAR STYLES (RESPONSIVE)
// ================================ */

// const styles = {
//   navbar: {
//     display: "flex",
//     alignItems: "center",
//     justifyContent: "space-between",
//     padding: "10px 14px",
//     background: "#ffffff",
//     borderBottom: "1px solid #ddd",
//     position: "sticky",
//     top: 0,
//     zIndex: 1000,
//     width: "100%",
//   },

//   /* LEFT */
//   leftBox: {
//     display: "flex",
//     alignItems: "center",
//     gap: 10,
//     position: "relative",
//     maxWidth: "70%",
//     flexWrap: "nowrap",
//   },

//   menuBtn: {
//     fontSize: 20,
//     fontWeight: "900",
//     background: "none",
//     border: "2px solid #4e54c8",
//     borderRadius: 6,
//     padding: "2px 8px",
//     cursor: "pointer",
//     color: "#4e54c8",
//     minWidth: 36,
//     minHeight: 32,
//   },

//   brand: {
//     fontSize: 16,
//     fontWeight: "700",
//     color: "#4e54c8",
//     whiteSpace: "nowrap",
//   },

//   leftDropdown: {
//     position: "absolute",
//     top: 38,
//     left: 0,
//     width: 230,
//     maxHeight: "70vh",
//     overflowY: "auto",
//     background: "#fff",
//     borderRadius: 12,
//     boxShadow: "0 6px 20px rgba(0,0,0,0.2)",
//     zIndex: 1000,
//   },

//   menuItem: {
//     display: "block",
//     padding: "12px 14px",
//     textDecoration: "none",
//     color: "#333",
//     borderBottom: "1px solid #eee",
//     fontWeight: 600,
//     fontSize: 14,
//   },

//   /* RIGHT */
//   userBox: {
//     position: "relative",
//     flexShrink: 0,
//   },

//   profileIconBtn: {
//     background: "none",
//     border: "none",
//     padding: 0,
//     cursor: "pointer",
//   },

//   avatar: {
//     width: 34,
//     height: 34,
//     borderRadius: "50%",
//     border: "2px solid #4e54c8",
//     objectFit: "cover",
//   },

//   userDropdown: {
//     position: "absolute",
//     right: 0,
//     top: 42,
//     width: 220,
//     maxWidth: "90vw",
//     background: "#fff",
//     borderRadius: 12,
//     boxShadow: "0 8px 20px rgba(0,0,0,0.2)",
//     overflow: "hidden",
//     zIndex: 1000,
//   },

//   userHeader: {
//     display: "flex",
//     gap: 10,
//     padding: 12,
//     background: "#f8f9fa",
//     borderBottom: "1px solid #eee",
//     alignItems: "center",
//   },

//   userBigAvatar: {
//     width: 38,
//     height: 38,
//     borderRadius: "50%",
//   },

//   userItem: {
//     display: "block",
//     padding: "10px 14px",
//     textDecoration: "none",
//     color: "#333",
//     fontWeight: 600,
//     fontSize: 14,
//   },

//   logoutBtn: {
//     width: "100%",
//     border: "none",
//     background: "#dc3545",
//     color: "#fff",
//     padding: 12,
//     fontWeight: 700,
//     cursor: "pointer",
//     fontSize: 14,
//   },
// };
